import React, { useMemo, useState } from "react";
import { Box, Typography, useTheme, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import {
  formatCurrency,
  formatNumber,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";

export type InsertionOrderMetricType =
  | "impressions"
  | "clicks"
  | "cost"
  | "realCost"
  | "ctr"
  | "all";

interface InsertionOrderMetricsChartProps {
  dailyData: components["schemas"]["DailyLineItemMetrics"][] | undefined;
  isLoading: boolean;
  title?: string;
  currency?: components["schemas"]["currency"];
  isAdmin?: boolean;
}

interface ChartDataPoint {
  date: string;
  impressions: number;
  clicks: number;
  cost: number;
  realCost: number;
  ctr: number;
}

const InsertionOrderMetricsChart: React.FC<InsertionOrderMetricsChartProps> = ({
  dailyData,
  isLoading,
  title = "Metrics Timeline",
  currency = "EUR",
  isAdmin = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedMetrics, setSelectedMetrics] = useState<
    InsertionOrderMetricType[]
  >(["all"]);

  // Define metric labels
  const metricLabels: Record<InsertionOrderMetricType, string> = {
    all: t("common.all", { defaultValue: "All" }),
    impressions: t("home.impressions", { defaultValue: "Impressions" }),
    clicks: t("home.clicks", { defaultValue: "Clicks" }),
    cost: t("home.dashboardInvestment", { defaultValue: "Investment" }),
    realCost: t("campaigns.realCost", { defaultValue: "Real Cost" }),
    ctr: t("home.ctr", { defaultValue: "CTR" }),
  };

  // Define colors for each metric to match MetricsTimelineChart
  const metricColors: Record<
    Exclude<InsertionOrderMetricType, "all">,
    string
  > = {
    ctr: theme.palette.primary.main,
    impressions: "#9c27b0", // Purple
    clicks: "#2196f3", // Blue
    cost: "#ff9800", // Orange
    realCost: theme.palette.secondary.main,
  };

  // Handle metric selection change
  const handleMetricChange = (
    event: React.MouseEvent<HTMLElement>,
    metric: InsertionOrderMetricType
  ) => {
    event.preventDefault();

    // Special handling for 'all' option
    if (metric === "all") {
      // If 'all' is already selected, do nothing
      if (selectedMetrics.includes("all")) {
        return;
      }
      // Otherwise, select only 'all'
      setSelectedMetrics(["all"]);
      return;
    }

    // If selecting a specific metric, remove 'all' if present
    let newSelectedMetrics = selectedMetrics.filter((m) => m !== "all");

    // Toggle the selected metric
    if (selectedMetrics.includes(metric)) {
      newSelectedMetrics = newSelectedMetrics.filter((m) => m !== metric);

      // If no metrics are selected, default to 'all'
      if (newSelectedMetrics.length === 0) {
        setSelectedMetrics(["all"]);
        return;
      }
    } else {
      newSelectedMetrics.push(metric);
    }

    setSelectedMetrics(newSelectedMetrics);
  };

  // Process data for the chart
  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (!dailyData) return [];

    return dailyData.map((day) => ({
      date: new Date(day.date).toLocaleDateString(),
      impressions: day.impressions,
      clicks: day.clicks,
      cost: day.cost,
      realCost: day.realCost || 0, // Ensure realCost is always a number
      ctr: day.ctr,
    }));
  }, [dailyData]);

  // Format tooltip values based on metric type
  const formatTooltipValue = (value: number, metricKey: string): string => {
    if (metricKey === "impressions" || metricKey === "clicks") {
      return formatNumber(value);
    } else if (metricKey === "ctr") {
      return formatPercentageNoMultiply(value);
    } else if (metricKey === "cost" || metricKey === "realCost") {
      return formatCurrency(value, currency);
    }
    return value.toString();
  };

  // Custom tooltip component
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: TooltipProps<number, string>) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    return (
      <Box
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          border: `1px solid ${theme.palette.divider}`,
          p: 1.5,
          borderRadius: 1,
          boxShadow: theme.shadows[2],
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}
        >
          {label}
        </Typography>
        {payload.map((entry) => {
          // Skip 'all' as it's just a selection option
          if (entry.dataKey === "all") return null;

          // For admin users, show all metrics
          // For non-admin users, hide realCost
          if (entry.dataKey === "realCost" && !isAdmin) return null;

          return (
            <Box
              key={entry.dataKey}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    backgroundColor: entry.color,
                    borderRadius: "50%",
                    mr: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {metricLabels[entry.dataKey as InsertionOrderMetricType] ||
                    entry.dataKey}
                  :
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  ml: 2,
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                }}
              >
                {formatTooltipValue(
                  entry.value as number,
                  entry.dataKey as string
                )}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 450,
          mt: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 450,
          mt: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {t("common.noData", { defaultValue: "No data available" })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: 450, mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: { xs: "wrap", md: "nowrap" },
          gap: { xs: 1, md: 0 },
        }}
      >
        <Typography
          variant="h6"
          fontWeight={500}
          color="text.primary"
          sx={{
            maxWidth: { xs: "100%", sm: "50%" },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mr: 1,
          }}
          title={title} // Show full title on hover
        >
          {title}
        </Typography>

        {/* Metric selection */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {Object.entries(metricLabels).map(([key, label]) => {
            const metricKey = key as InsertionOrderMetricType;
            const isSelected = selectedMetrics.includes(metricKey);

            // Hide Real Cost for non-admin users
            if (metricKey === "realCost" && !isAdmin) {
              return null;
            }

            return (
              <Box
                key={metricKey}
                component="button"
                onClick={(e) => handleMetricChange(e, metricKey)}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "16px",
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: isSelected
                    ? metricKey === "all"
                      ? theme.palette.text.primary
                      : metricColors[
                          metricKey as Exclude<InsertionOrderMetricType, "all">
                        ]
                    : "transparent",
                  color: isSelected ? "white" : "inherit",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  fontFamily: "inherit",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: isSelected
                      ? theme.palette.action.selected
                      : theme.palette.action.hover,
                  },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>
      </Box>
      <ResponsiveContainer width="100%" height="90%">
        <LineChart
          data={chartData}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="date"
            tick={{ fill: theme.palette.text.secondary }}
            axisLine={{ stroke: theme.palette.divider }}
            tickLine={{ stroke: theme.palette.divider }}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: theme.palette.text.secondary }}
            axisLine={{ stroke: theme.palette.divider }}
            tickLine={{ stroke: theme.palette.divider }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fill: theme.palette.text.secondary }}
            axisLine={{ stroke: theme.palette.divider }}
            tickLine={{ stroke: theme.palette.divider }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{
              paddingTop: 10,
              paddingBottom: 10,
              width: "95%",
              margin: "0 auto",
              fontSize: "0.75rem",
            }}
            align="center"
            verticalAlign="bottom"
            iconSize={10}
            iconType="circle"
            layout="horizontal"
          />

          {/* Render lines based on selected metrics */}
          {(selectedMetrics.includes("all") ||
            selectedMetrics.includes("impressions")) && (
            <Line
              type="monotone"
              dataKey="impressions"
              name={metricLabels.impressions}
              stroke={metricColors.impressions}
              yAxisId="left"
              dot={false}
              activeDot={{ r: 6 }}
            />
          )}

          {(selectedMetrics.includes("all") ||
            selectedMetrics.includes("clicks")) && (
            <Line
              type="monotone"
              dataKey="clicks"
              name={metricLabels.clicks}
              stroke={metricColors.clicks}
              yAxisId="left"
              dot={false}
              activeDot={{ r: 6 }}
            />
          )}

          {(selectedMetrics.includes("all") ||
            selectedMetrics.includes("ctr")) && (
            <Line
              type="monotone"
              dataKey="ctr"
              name={metricLabels.ctr}
              stroke={metricColors.ctr}
              yAxisId="right"
              dot={false}
              activeDot={{ r: 6 }}
            />
          )}

          {(selectedMetrics.includes("all") ||
            selectedMetrics.includes("cost")) && (
            <Line
              type="monotone"
              dataKey="cost"
              name={metricLabels.cost}
              stroke={metricColors.cost}
              yAxisId="right"
              dot={false}
              activeDot={{ r: 6 }}
            />
          )}

          {/* Only show realCost for admin users */}
          {isAdmin &&
            (selectedMetrics.includes("all") ||
              selectedMetrics.includes("realCost")) && (
              <Line
                type="monotone"
                dataKey="realCost"
                name={metricLabels.realCost}
                stroke={metricColors.realCost}
                yAxisId="right"
                dot={false}
                activeDot={{ r: 6 }}
              />
            )}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default InsertionOrderMetricsChart;
